import { useState } from 'react';
import { Button } from './ui.js';

/**
 * A freshly issued set of recovery codes, with the two ways of keeping them.
 *
 * Codes are shown exactly once, so the moment they are on screen is the only
 * moment they can be saved. Reading ten of them off a screen and typing them
 * somewhere else is where people give up and close the tab, which is how an
 * account ends up with no way back in — hence a button that puts them on the
 * clipboard for a password manager, and one that writes a file for a printer
 * or a safe.
 *
 * Enrollment and the account page both show this, so the set someone saves is
 * presented the same way whichever screen issued it.
 */
export function RecoveryCodes({ codes, filename }: { codes: string[]; filename: string }) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const asText = `${codes.join('\n')}\n`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(asText);
      setCopied(true);
      setCopyFailed(false);
    } catch {
      // Denied permission, or an insecure origin. Nothing to do about it here,
      // but silently doing nothing would read as a broken button.
      setCopyFailed(true);
      setCopied(false);
    }
  }

  function download() {
    const url = URL.createObjectURL(new Blob([asText], { type: 'text/plain;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    // The blob would otherwise be held until the page is left.
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-3">
      <ul className="grid grid-cols-2 gap-2 rounded-lg bg-sunken p-4 font-mono text-sm text-body">
        {codes.map((code) => (
          <li key={code} className="tabular">
            {code}
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="secondary" onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button type="button" variant="secondary" onClick={download}>
          Download
        </Button>
        {copyFailed && (
          <span className="text-xs text-muted">
            This browser would not let the page use the clipboard. Select the codes and copy them,
            or download the file.
          </span>
        )}
      </div>
    </div>
  );
}
